import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useTamanhos } from "../../../hooks/useTamanhos";
import { useBordas } from "../../../hooks/useBordas";
import {
  useCreateProducao,
  useFinalizarProducao,
  useDeleteProducao,
} from "../../../hooks/useProducaoMutations";
import { useAuth } from "../../../context/AuthContext";
import { Tamanho } from "../../../services/tamanhoService";
import { Borda } from "../../../services/bordaService";
import { producaoService } from "../../../services/producaoService";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { COLORS } from "../../../constants";

interface PizzaioloContextType {
  isModalOpen: boolean;
  showTimer: boolean;
  selectedPizza: string;
  selectedBorda: number | null;
  timeElapsed: number;
  tamanhos: Tamanho[];
  isLoadingTamanhos: boolean;
  bordas: Borda[];
  isLoadingBordas: boolean;
  isCreatingProducao: boolean;
  isFinalizingProducao: boolean;
  handlePizzaClick: (sizeName: string) => Promise<void>;
  handleCancel: () => void;
  handleConfirm: () => void;
  handleStop: () => void;
  setSelectedBorda: (bordaId: number | null) => void;
}

const PizzaioloContext = createContext<PizzaioloContextType | undefined>(
  undefined
);

export const PizzaioloProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [selectedPizza, setSelectedPizza] = useState<string>("");
  const [selectedBorda, setSelectedBorda] = useState<number | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [producaoId, setProducaoId] = useState<number | null>(null);

  const { user } = useAuth();
  const { data: tamanhos = [], isLoading: isLoadingTamanhos } = useTamanhos(
    user?.lojaId ? { lojaId: user.lojaId } : undefined
  );
  const { data: bordas = [], isLoading: isLoadingBordas } = useBordas();
  const createProducaoMutation = useCreateProducao();
  const finalizarProducaoMutation = useFinalizarProducao();
  const deleteProducaoMutation = useDeleteProducao();

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (showTimer) {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showTimer]);

  const handlePizzaClick = async (sizeName: string) => {
    setSelectedPizza(sizeName);

    if (sizeName === "Pizza Erro") {
      if (!user) {
        Swal.fire("Erro", "Usuário não encontrado", "error");
        return;
      }

      try {
        const producaoEmAndamento =
          await producaoService.getProducaoEmAndamento(user.id);

        if (producaoEmAndamento) {
          const result = await Swal.fire({
            title: "Produção em andamento",
            text: "Você já tem uma produção em andamento. Deseja finalizá-la antes de criar uma nova?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: COLORS.PRIMARY,
            cancelButtonColor: COLORS.NEUTRAL_500,
            confirmButtonText: "Sim, finalizar e criar nova",
            cancelButtonText: "Cancelar",
          });

          if (result.isConfirmed) {
            await finalizarProducaoMutation.mutateAsync(producaoEmAndamento.id);
            toast.success("Produção anterior finalizada");
          } else {
            return;
          }
        }

        const tamanhoSelecionado = tamanhos.find((t) => t.nome === sizeName);

        if (!tamanhoSelecionado) {
          Swal.fire("Erro", "Tamanho 'Pizza Erro' não encontrado", "error");
          return;
        }

        let bordaErro = bordas.find((b) => b.nome.toLowerCase() === "erro");
        if (!bordaErro && bordas.length > 0) {
          bordaErro = bordas[0];
        }

        if (!bordaErro) {
          Swal.fire(
            "Erro",
            "Nenhuma borda disponível. Por favor, cadastre uma borda primeiro.",
            "error"
          );
          return;
        }

        const producao = await createProducaoMutation.mutateAsync({
          lojaId: user.lojaId,
          usuarioId: user.id,
          bordaId: bordaErro.id,
          tamanhoId: tamanhoSelecionado.id,
        });

        setProducaoId(producao.id);
        setTimeElapsed(0);
        setShowTimer(true);
        setSelectedBorda(null);
        toast.success("Produção de Pizza Erro iniciada!");
      } catch (error: any) {
        const errorMessage = error?.message || "Erro ao criar produção";
        Swal.fire("Erro", errorMessage, "error");
      }
    } else {
      setSelectedBorda(null);
      setIsModalOpen(true);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedPizza("");
    setSelectedBorda(null);
  };

  const handleConfirm = async () => {
    if (!user || !selectedBorda || !selectedPizza) {
      Swal.fire("Erro", "Dados incompletos para criar produção", "error");
      return;
    }

    try {
      const producaoEmAndamento = await producaoService.getProducaoEmAndamento(
        user.id
      );

      if (producaoEmAndamento) {
        const result = await Swal.fire({
          title: "Produção em andamento",
          text: "Você já tem uma produção em andamento. Deseja finalizá-la antes de criar uma nova?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: COLORS.PRIMARY,
          cancelButtonColor: "#6b7280",
          confirmButtonText: "Sim, finalizar e criar nova",
          cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
          await finalizarProducaoMutation.mutateAsync(producaoEmAndamento.id);
          toast.success("Produção anterior finalizada");
        } else {
          return;
        }
      }
    } catch (error) {
      console.error("Erro ao verificar produção em andamento:", error);
    }

    const tamanhoSelecionado = tamanhos.find((t) => t.nome === selectedPizza);

    if (!tamanhoSelecionado) {
      Swal.fire("Erro", "Tamanho não encontrado", "error");
      return;
    }

    try {
      const producao = await createProducaoMutation.mutateAsync({
        lojaId: user.lojaId,
        usuarioId: user.id,
        bordaId: selectedBorda,
        tamanhoId: tamanhoSelecionado.id,
      });

      setProducaoId(producao.id);
      setIsModalOpen(false);
      setTimeElapsed(0);
      setShowTimer(true);
      setSelectedBorda(null);
      toast.success("Produção iniciada com sucesso!");
    } catch (error: any) {
      const errorMessage = error?.message || "Erro ao criar produção";

      if (errorMessage.includes("dados únicos")) {
        Swal.fire({
          title: "Erro",
          text: "Já existe uma produção em andamento. Por favor, finalize a produção atual antes de criar uma nova.",
          icon: "error",
          confirmButtonColor: COLORS.PRIMARY,
        });
      } else {
        Swal.fire("Erro", errorMessage, "error");
      }
    }
  };

  const handleStop = async () => {
    if (!producaoId) {
      setShowTimer(false);
      setTimeElapsed(0);
      setSelectedPizza("");
      setProducaoId(null);
      return;
    }

    try {
      if (timeElapsed < 10) {
        await deleteProducaoMutation.mutateAsync(producaoId);
        toast.info("Produção cancelada (tempo muito curto)");
      } else {
        await finalizarProducaoMutation.mutateAsync(producaoId);
        toast.success("Produção finalizada com sucesso!");
      }
      setShowTimer(false);
      setTimeElapsed(0);
      setSelectedPizza("");
      setProducaoId(null);
    } catch (error: any) {
      Swal.fire(
        "Erro",
        error?.message || "Erro ao processar produção",
        "error"
      );
    }
  };

  return (
    <PizzaioloContext.Provider
      value={{
        isModalOpen,
        showTimer,
        selectedPizza,
        selectedBorda,
        timeElapsed,
        tamanhos,
        isLoadingTamanhos,
        bordas,
        isLoadingBordas,
        isCreatingProducao: createProducaoMutation.isPending,
        isFinalizingProducao:
          finalizarProducaoMutation.isPending ||
          deleteProducaoMutation.isPending,
        handlePizzaClick,
        handleCancel,
        handleConfirm,
        handleStop,
        setSelectedBorda,
      }}
    >
      {children}
    </PizzaioloContext.Provider>
  );
};

export const usePizzaiolo = () => {
  const context = useContext(PizzaioloContext);
  if (!context) {
    throw new Error("usePizzaiolo must be used within PizzaioloProvider");
  }
  return context;
};
